import * as React from "react";
import { observer } from "mobx-react-lite";
import { Table } from "antd";
import dayjs from "dayjs";
import { TaskViewVisualStore } from "../stores";
import { Utils } from "../../common/misc/Utils";
import { MetadataAudit } from "../types";
import ActorBadge from "./ActorBadge";
import AppModal from "../../common/components/AppModal";

type Props = {
    store: TaskViewVisualStore;
    onClose: () => void;
    allUsersFullNameResolver: { [key: string]: string };
};

const MetadataChangesDialog: React.FC<Props> = ({
    store,
    onClose,
    allUsersFullNameResolver,
}) => {
    const getMetadataParseValue = (fieldName: string, nativeValue: string) => {
        const metadata = store.metadataDefinitions.find(
            (md) => md.name === fieldName,
        );
        return Utils.getMetadataParseValue(nativeValue, metadata!);
    };
    const columns = [
        {
            title: "Field",
            dataIndex: "fieldName",
            key: "fieldName",
            width: "16%",
            render: (value: string) =>
                store.metadataDefinitions.find((md) => md.name === value)
                    ?.title || value,
        },
        {
            title: "Old value",
            dataIndex: "oldValue",
            key: "oldValue",
            width: "20%",
            render: (
                value: string | null | undefined,
                record: MetadataAudit,
            ) =>
                value === null || value === undefined
                    ? "-"
                    : getMetadataParseValue(record.fieldName, value),
        },
        {
            title: "New value",
            dataIndex: "newValue",
            key: "newValue",
            width: "20%",
            render: (
                value: string | null | undefined,
                record: MetadataAudit,
            ) =>
                value === null || value === undefined
                    ? "-"
                    : getMetadataParseValue(record.fieldName, value),
        },
        {
            title: "Changed by",
            dataIndex: "updatedBy",
            key: "updatedBy",
            width: "20%",
            render: (userId: string, record: MetadataAudit) => (
                <ActorBadge
                    actor={record.updatedByActor}
                    legacyUserId={userId}
                    legacyUserName={allUsersFullNameResolver[userId]}
                    store={store}
                    showName
                />
            ),
        },
        {
            title: "Date",
            dataIndex: "timestamp",
            key: "timestamp",
            width: "150px",
            render: (timestamp: string) =>
                dayjs(timestamp).format("DD MMM YYYY, HH:mm"),
        },
        {
            title: "Source",
            dataIndex: "source",
            key: "source",
        },
    ];

    return (
        <AppModal
            title={`Metadata changes`}
            open={store.isMetadataChangesDialogVisible}
            onCancel={onClose}
            footer={null}
            width={1300}
            centered
            className="metadata-changes-modal"
        >
            <Table
                columns={columns}
                dataSource={store.metadataAudits}
                rowKey="id"
                pagination={false}
                size="small"
                scroll={{ y: 400 }}
            />
        </AppModal>
    );
};

export default observer(MetadataChangesDialog);
